import { RADashboardCardId } from './cards-enums'
import { FADashboardFullType, FADashboardType } from './dashboards/dashboard-types'

const isMatchingDashboardType = (
  exclusiveType: FADashboardType | FADashboardFullType,
  dashboardType: FADashboardType,
) =>
  exclusiveType === dashboardType || (exclusiveType as string).startsWith(`${dashboardType} > `)

/** Lookup specifying where a card must render exclusively. If the array is empty then the card can be rendered on any dashboard  */
export const CardExclusivityLookup: Record<RADashboardCardId, (FADashboardType | FADashboardFullType)[]> =
  Object.freeze({
    [RADashboardCardId.FactorLensRisk]: [FADashboardFullType.Risk_Risk],
    // Split-out Factor Lens cards — reuse the existing FactorLens source.
    [RADashboardCardId.FactorLensGridRisk]: [FADashboardType.Risk],
    [RADashboardCardId.FactorWaterfallRisk]: [FADashboardType.Risk],
    [RADashboardCardId.FactorBreakdownRisk]: [FADashboardType.Risk],
    [RADashboardCardId.MetricsSummaryRisk]: [FADashboardType.Risk],
    [RADashboardCardId.HoldingsContributionRisk]: [FADashboardType.Risk],
    [RADashboardCardId.Point_DV01]: [FADashboardType.Risk],
    [RADashboardCardId.Point_Duration]: [FADashboardType.Risk],
    [RADashboardCardId.Point_Convexity]: [FADashboardType.Risk],
    [RADashboardCardId.Point_YieldToMaturity]: [FADashboardType.Risk],
    [RADashboardCardId.Point_SpreadDuration]: [FADashboardType.Risk],
    [RADashboardCardId.Point_YearsToMaturity]: [FADashboardType.Risk],
    [RADashboardCardId.Point_YieldToWorst]: [FADashboardType.Risk],
    [RADashboardCardId.Point_CouponRate]: [FADashboardType.Risk],
    [RADashboardCardId.Point_DividendYield]: [FADashboardType.Risk],
    [RADashboardCardId.Point_AverageLife]: [FADashboardType.Risk],
    [RADashboardCardId.Point_Beta]: [FADashboardType.Risk],
    [RADashboardCardId.Point_BookToPrice]: [FADashboardType.Risk],
    [RADashboardCardId.Point_BookValue]: [FADashboardType.Risk],
    [RADashboardCardId.Point_CashFlowYield]: [FADashboardType.Risk],
    [RADashboardCardId.Point_CurrentYield]: [FADashboardType.Risk],
    [RADashboardCardId.Point_EPRatio]: [FADashboardType.Risk],
    [RADashboardCardId.Point_EarningsPerShare]: [FADashboardType.Risk],
    [RADashboardCardId.Point_EffectiveD2]: [FADashboardType.Risk],
    [RADashboardCardId.Point_EffectiveD3]: [FADashboardType.Risk],
    [RADashboardCardId.Point_DurContributionPct]: [FADashboardType.Risk],
    [RADashboardCardId.Point_DurContributionYrs]: [FADashboardType.Risk],
    [RADashboardCardId.Point_EffectiveSpreadDuration]: [FADashboardType.Risk],
    [RADashboardCardId.Point_SpreadDurContributionPct]: [FADashboardType.Risk],
    [RADashboardCardId.Point_SpreadDurContributionYrs]: [FADashboardType.Risk],
    [RADashboardCardId.Point_GainLoss]: [FADashboardType.Risk],
    [RADashboardCardId.Point_MacaulayDuration]: [FADashboardType.Risk],
    [RADashboardCardId.Point_MarketCap]: [FADashboardType.Risk],
    [RADashboardCardId.Point_MarketCapLog]: [FADashboardType.Risk],
    [RADashboardCardId.Point_MarketValue]: [FADashboardType.Risk],
    [RADashboardCardId.Point_MarketValuePar]: [FADashboardType.Risk],
    [RADashboardCardId.Point_ModifiedDuration]: [FADashboardType.Risk],
    [RADashboardCardId.Point_Momentum]: [FADashboardType.Risk],
    [RADashboardCardId.Point_MoodyQuality]: [FADashboardType.Risk],
    [RADashboardCardId.Point_NotionalValue]: [FADashboardType.Risk],
    [RADashboardCardId.Point_OAS]: [FADashboardType.Risk],
    [RADashboardCardId.Point_PBRatio]: [FADashboardType.Risk],
    [RADashboardCardId.Point_PERatio]: [FADashboardType.Risk],
    [RADashboardCardId.Point_PctFaceMarketValue]: [FADashboardType.Risk],
    [RADashboardCardId.Point_PctMarketValue]: [FADashboardType.Risk],
    [RADashboardCardId.Point_Price]: [FADashboardType.Risk],
    [RADashboardCardId.Point_SPQuality]: [FADashboardType.Risk],
    [RADashboardCardId.Point_SecurityCount]: [FADashboardType.Risk],
    [RADashboardCardId.Point_TotalAccruedInterest]: [FADashboardType.Risk],
    [RADashboardCardId.Point_TrailingEPS]: [FADashboardType.Risk],
    [RADashboardCardId.Point_Volatility]: [FADashboardType.Risk],
    [RADashboardCardId.Point_YearsToWorst]: [FADashboardType.Risk],
    // End extended data point cards
    [RADashboardCardId.Point_TrailingRisk]: [FADashboardType.Risk],
    [RADashboardCardId.HoldingsTopBottom]: [FADashboardType.Risk],
    [RADashboardCardId.HoldingsRiskVisualization]: [FADashboardType.Risk],
    // Attribution cards — factor attribution only
    [RADashboardCardId.Return_Portfolio]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Return_Benchmark]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Return_Active]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Chart_DailyReturn]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Chart_PortfolioReturn]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Chart_BenchmarkReturn]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Chart_ActiveReturn]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Chart_FactorAttribution]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Table_Holdings]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Table_HoldingsTopBottom]: [FADashboardFullType.Attribution_FactorAttribution],
    [RADashboardCardId.Chart_HoldingsBubble]: [FADashboardFullType.Attribution_FactorAttribution],
  });

/** Whether a card is allowed to render on a dashboard of the given type. Cards with an empty exclusivity list are allowed on any dashboard. */
export const isCardAllowedForDashboardType = (
  cardId: RADashboardCardId,
  dashboardType: FADashboardType,
): boolean => {
  const exclusivity = CardExclusivityLookup[cardId]
  return exclusivity.length === 0 || exclusivity.some(type => isMatchingDashboardType(type, dashboardType))
}
